import { loadAuth, accessTokenExpired } from './js/state.js';
import { router } from './js/router.js';
import { registerSW, setupPushNotifications } from './js/pwa.js';
import { updateNotifBadge, showToast, refreshActionCounts } from './js/shell.js';
import { checkStaleNotification, dismissStaleBanner } from './js/stale-banner.js';
import { initTheme } from './js/theme.js';
import { recoverSSE } from './js/sse-reconnect.js';
import { handleResume } from './js/resume.js';
import { applyIdentityProfile, identityNotificationType } from './js/identity-sync.js';

// Apply theme before first paint to avoid flash.
initTheme();

// Log build version so we can verify which deploy is running.
const appVersion = document.querySelector('meta[name="app-version"]')?.content || 'unknown';
console.log(`%cField Whisper ${appVersion}`, 'color:#1e40af;font-weight:bold');

// Expose router globally so practices.js can call it after save
// without creating a circular import.
window.__router = router;

loadAuth();
window.addEventListener('hashchange', () => { dismissStaleBanner(); router(); });

(async () => {
  await router();
  // Attempt push setup if already authenticated.
  const { state } = await import('./js/state.js');
  if (state.token) setupPushNotifications();

  startNotifLiveUpdates(state);
})();

registerSW();

// Real-time notification delivery uses two complementary channels:
//
//   1. SSE (Server-Sent Events) — instant delivery while the app tab is open.
//      Connects to /api/v1/notifications/stream and receives each notification
//      as it is created server-side. Auto-reconnects with exponential backoff
//      using the latest access token.
//
//   2. Service-worker web push — delivers browser notifications when the app
//      is backgrounded or closed. The SW broadcasts NOTIFICATION_RECEIVED to
//      any open tabs so the badge updates there too.

// Notification types that change the user's identity (roles, team associations).
// When one arrives, silently refresh the JWT and reload session data so the
// UI reflects the new permissions without a manual sign-out/sign-in.
let _identityRefreshPending = false;

async function refreshIdentity(state) {
  if (_identityRefreshPending) return;
  _identityRefreshPending = true;
  try {
    const { tryRefresh, api } = await import('./js/api.js');
    const { selectSeason } = await import('./js/shell.js');
    // 1. Fresh JWT → updated roles in state.roles.
    await tryRefresh();
    // 2. Reload teams → updated coach associations in state.teams.
    if (state.currentSeasonId) await selectSeason(state.currentSeasonId);
    // 3. `/users/me` is authoritative for roles and delegated permissions.
    const me = await api.getMe().catch(() => null);
    if (me) applyIdentityProfile(state, me);
    // 4. Rebuild shell + re-render current page.
    if (window.__router) window.__router();
  } finally {
    _identityRefreshPending = false;
  }
}

let _sseRetryDelay = 3000;

// Liveness tracking: the server emits an `hb` event every 30s. A connection
// that died without an error event (device sleep, NAT timeout) keeps
// readyState OPEN forever — silence is the only symptom, so record when we
// last heard anything and treat prolonged silence as dead.
const SSE_STALE_MS = 75000; // two missed 30s heartbeats + margin
let _sseLastBeatAt = 0;

// sseAlive: the stream exists, isn't closed, and has been heard from recently.
function sseAlive(state) {
  return !!state._sseSource &&
    state._sseSource.readyState !== EventSource.CLOSED &&
    (Date.now() - _sseLastBeatAt) < SSE_STALE_MS;
}

function connectSSE(state) {
  if (!state.token) return;
  if (state._sseSource) {
    state._sseSource.close();
    state._sseSource = null;
  }

  const url = '/api/v1/notifications/stream?token=' + encodeURIComponent(state.token);
  const es = new EventSource(url);
  state._sseSource = es;
  _sseLastBeatAt = Date.now(); // the connect attempt counts as liveness

  es.onopen = () => { _sseRetryDelay = 3000; _sseLastBeatAt = Date.now(); };

  es.addEventListener('hb', () => { _sseLastBeatAt = Date.now(); });

  es.addEventListener('notification', event => {
    _sseLastBeatAt = Date.now();
    try {
      const n = JSON.parse(event.data);
      updateNotifBadge((state.notifCount || 0) + 1);
      // A notification usually means someone else changed something the
      // Actions badge counts (a new request, a report, a BAYS sync).
      refreshActionCounts();
      if (!document.hidden) {
        if (n.type === 'announcement') {
          // Lazy-load the announcement alert (and its heavy transitive deps:
          // announcements/teams/coach/timepicker) only when one actually
          // arrives, keeping ~250KB off the cold-load path.
          import('./js/notifications.js').then(m => m.showAnnouncementAlert(n));
        } else {
          showToast(n.title, n.body);
        }
        checkStaleNotification(n.type);
      }
      // Identity-affecting notifications: auto-refresh auth + session data.
      if (identityNotificationType(n.type)) refreshIdentity(state);
    } catch { /* ignore parse errors */ }
  });

  // Permission changes are authorization state, so they bypass notification
  // preferences and update every connected session immediately.
  es.addEventListener('permissions_changed', () => {
    _sseLastBeatAt = Date.now();
    refreshIdentity(state);
  });

  es.onerror = () => {
    es.close();
    state._sseSource = null;
    if (!state.token) return;
    const delay = _sseRetryDelay;
    _sseRetryDelay = Math.min(_sseRetryDelay * 2, 60000);
    // The token may have silently expired (the server's 401 surfaces only as a
    // generic error). Refresh before retrying so we don't loop on a stale token;
    // recoverSSE reconnects with the existing token only if the refresh fails.
    setTimeout(async () => {
      // The resume handler may have already re-established the stream while
      // this backoff was pending — don't tear its connection down.
      if (state._sseSource) return;
      const { tryRefresh } = await import('./js/api.js');
      await recoverSSE({
        hasToken: () => !!state.token,
        tryRefresh,
        reconnect: () => connectSSE(state),
      });
    }, delay);
  };
}

function startNotifLiveUpdates(state) {
  // 1. SSE connection for instant in-app delivery.
  connectSSE(state);

  // 2. Service-worker message listener (for push notifications when backgrounded).
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', event => {
      if (event.data?.type !== 'NOTIFICATION_RECEIVED') return;
      updateNotifBadge((state.notifCount || 0) + 1);
      refreshActionCounts();
      if (!document.hidden) {
        showToast(event.data.title, event.data.body);
      }
    });
  }

  // 3. App-resume liveness. A sleep/background period silently kills the
  // network connections and lets the 15-min access token lapse; without this,
  // all of that is repaired lazily at the user's first tap — the random
  // "10s+ tab switch" stall. Repair it at wake instead: refresh a stale token
  // now (which also reconnects SSE) and revive a dead/zombie stream without
  // waiting out the error-path backoff.
  let _lastResumeAt = 0;
  const onAppResume = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - _lastResumeAt < 1000) return; // wake fires several events in a burst
    _lastResumeAt = now;
    // Back on the tab: other people's changes while away (their notifications
    // may have gone to a backgrounded push) show without a reload.
    refreshActionCounts({ olderThanMs: 30000 });
    import('./js/api.js').then(({ tryRefresh }) => handleResume({
      hasToken: () => !!state.token,
      tokenStale: () => accessTokenExpired(30),
      tryRefresh,
      sseAlive: () => sseAlive(state),
      reconnect: () => connectSSE(state),
    }));
  };
  document.addEventListener('visibilitychange', onAppResume);
  window.addEventListener('pageshow', onAppResume);
  window.addEventListener('online', onAppResume);
  window.addEventListener('focus', onAppResume);

  // 4. Zombie sweep while the app stays visible: a stream that died without
  // an error event keeps readyState OPEN but goes silent. The resume handler
  // only runs on wake, so also reconnect when heartbeats stop mid-session.
  setInterval(() => {
    if (document.visibilityState !== 'visible' || !state.token) return;
    if (state._sseSource &&
        state._sseSource.readyState === EventSource.OPEN &&
        (Date.now() - _sseLastBeatAt) > SSE_STALE_MS) {
      connectSSE(state);
    }
  }, 45000);
}

// Expose connectSSE so login/token-refresh can re-establish the connection.
window.__reconnectSSE = () => {
  import('./js/state.js').then(({ state }) => connectSSE(state));
};
